/**
 * Company details for invoices (Enterprise and anyone paying by invoice).
 * They live on the organization's Stripe customer, which prints them on
 * every invoice; the VAT or other tax number is a Stripe tax ID, so Stripe
 * validates it (EU numbers also against VIES). This file only shapes and
 * pre-checks what people type, so mistakes show before Stripe is called.
 */

export interface BillingAddress {
  line1: string;
  line2: string;
  postalCode: string;
  city: string;
  state: string;
  /** ISO 3166-1 alpha-2, e.g. "NO". */
  country: string;
}

export interface BillingDetails {
  /** Legal company name, as invoices should show it. */
  companyName: string;
  /** Where invoices and receipts go. */
  email: string;
  address: BillingAddress;
  /** VAT / GST / tax number ('' = none). */
  taxId: string;
  /** Purchase order number printed on invoices ('' = none). */
  poNumber: string;
}

export const EMPTY_BILLING_DETAILS: BillingDetails = {
  companyName: '',
  email: '',
  address: { line1: '', line2: '', postalCode: '', city: '', state: '', country: '' },
  taxId: '',
  poNumber: '',
};

/** Payment terms a superadmin can grant (days until an invoice is due). */
export const PAYMENT_TERMS_DAYS = [14, 30, 45, 60] as const;

const EU = ['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK'];

/** Stripe tax ID type per country. */
const TAX_ID_TYPES: Record<string, string> = {
  ...Object.fromEntries(EU.map((c) => [c, 'eu_vat'])),
  GB: 'gb_vat',
  NO: 'no_vat',
  CH: 'ch_vat',
  IS: 'is_vat',
  LI: 'li_uid',
  AU: 'au_abn',
  NZ: 'nz_gst',
  CA: 'ca_bn',
  US: 'us_ein',
  IN: 'in_gst',
  JP: 'jp_cn',
  SG: 'sg_uen',
  ZA: 'za_vat',
  AE: 'ae_trn',
  SA: 'sa_vat',
  BR: 'br_cnpj',
  MX: 'mx_rfc',
  KR: 'kr_brn',
  TR: 'tr_tin',
  IL: 'il_vat',
  UA: 'ua_vat',
  RS: 'rs_pib',
};

/** Countries offered for billing addresses (ISO codes; names come from Intl.DisplayNames). */
export const BILLING_COUNTRIES = [...new Set([...Object.keys(TAX_ID_TYPES), 'AR', 'CL', 'CO', 'HK', 'ID', 'MY', 'PH', 'TH', 'TW', 'VN', 'EG', 'MA', 'NG', 'KE'])].sort();

/** The Stripe tax ID type for a country, or null where none is supported. */
export function taxIdTypeFor(country: string): string | null {
  return TAX_ID_TYPES[country.toUpperCase()] ?? null;
}

/** What the tax number is called in a country (for labels). */
export function taxIdLabel(country: string): string {
  const t = taxIdTypeFor(country);
  if (!t) return 'Tax ID';
  if (t.endsWith('_vat') || t === 'li_uid') return 'VAT number';
  if (t.endsWith('_gst')) return 'GST number';
  if (t === 'au_abn') return 'ABN';
  if (t === 'us_ein') return 'EIN';
  return 'Tax ID';
}

/** EU VAT numbers start with the country code; Greece uses EL. */
const vatPrefix = (country: string) => (country === 'GR' ? 'EL' : country);

/**
 * Tidies a tax number the way Stripe expects it: no spaces, dots or dashes
 * in VAT numbers, upper case, and the country prefix added to EU and UK VAT
 * numbers typed without it.
 */
export function normalizeTaxId(country: string, value: string): string {
  const c = country.toUpperCase();
  const type = taxIdTypeFor(c);
  const v = value.trim();
  if (!v) return '';
  if (type === 'eu_vat' || type === 'gb_vat' || type === 'no_vat') {
    let s = v.toUpperCase().replace(/[\s.\-]/g, '');
    const prefix = type === 'gb_vat' ? 'GB' : type === 'no_vat' ? 'NO' : vatPrefix(c);
    if (/^\d/.test(s)) s = prefix + s;
    // Norwegian VAT numbers end with MVA (the VAT register's suffix).
    if (type === 'no_vat' && !s.endsWith('MVA')) s += 'MVA';
    return s;
  }
  return v.replace(/\s+/g, ' ');
}

/** Checks a (normalized) tax number's shape; returns a message, or null when it looks right. */
export function checkTaxId(country: string, value: string): string | null {
  if (!value) return null;
  const c = country.toUpperCase();
  const type = taxIdTypeFor(c);
  if (!type) return null;
  if (type === 'eu_vat') {
    const prefix = vatPrefix(c);
    if (!value.startsWith(prefix)) return `A VAT number from this country starts with ${prefix}`;
    if (!/^[A-Z]{2}[0-9A-Z+*]{2,13}$/.test(value)) return 'That does not look like a VAT number';
  }
  if (type === 'gb_vat' && !/^GB(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/.test(value)) return 'A UK VAT number is GB and 9 digits';
  if (type === 'no_vat' && !/^NO\d{9}MVA$/.test(value)) return 'A Norwegian VAT number is NO, 9 digits and MVA';
  if (value.length > 40) return 'That tax number is too long';
  return null;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Problems with billing details, by field ('' when none). Invoices need a
 * company name, an email and a full address; the tax number is optional.
 */
export function billingDetailsErrors(d: BillingDetails): Partial<Record<'companyName' | 'email' | 'line1' | 'city' | 'postalCode' | 'country' | 'taxId' | 'poNumber', string>> {
  const e: ReturnType<typeof billingDetailsErrors> = {};
  if (!d.companyName.trim()) e.companyName = 'Enter the company name';
  else if (d.companyName.length > 200) e.companyName = 'Too long';
  if (!EMAIL.test(d.email.trim())) e.email = 'Enter the email invoices go to';
  if (!d.address.line1.trim()) e.line1 = 'Enter the street address';
  if (!d.address.city.trim()) e.city = 'Enter the city';
  if (!d.address.postalCode.trim() && d.address.country !== 'AE' && d.address.country !== 'HK') e.postalCode = 'Enter the postal code';
  if (!/^[A-Z]{2}$/.test(d.address.country)) e.country = 'Choose a country';
  const tax = checkTaxId(d.address.country, normalizeTaxId(d.address.country, d.taxId));
  if (tax) e.taxId = tax;
  if (d.poNumber.length > 60) e.poNumber = 'Too long';
  return e;
}
